/**
 * [LIQUID-GLASS 2026-11-02, user] The ColorOS 17 "Liquid Acrylic" look, as ONE recipe for every part that uses it.
 * The user picked it on the side-by-side comparison page (https://claude.ai/artifact/8qAJp3AydtAn1xEw4kNsU5) and said
 * "go for it and make the change properly to the app". Parts that use it so far: the App Alerts and Trash panels, the
 * Payments filter buttons + Edit Columns, and the Edit Mapping window.
 *
 * What it is: a see-through tint with a faint frosted grain, a sharp light line on the TOP edge and a soft reflection on
 * the BOTTOM edge (the contour rim), and a light shadow. The blur and colour boost are softer than the old
 * blur(48px) saturate(180%) glass, which amplified the colours behind it into a muddy tint. Controls are capsules that
 * squish a little when pressed and glow where you press (startLiquidPress, called once from index.tsx; the user OK'd a
 * small bounce on controls only). Tables, numbers and charts never get any of this.
 *
 * Pieces: lgSurface (panels, windows, list shells; the ONLY piece with a live blur), lgCard (cards on a glass surface; no
 * blur of their own), lgControl (capsule glass), lgRim (the contour rim, a ::before), lgPress (squish + press light, an
 * ::after), lgSunken (input boxes), lgChase (a soft light moving along a progress bar), lgSquircle (smooth corners in
 * Chrome / Edge 139+, plain round corners elsewhere) and the three buttons.
 * Put a mixin FIRST in a component and keep only layout after it, like styles/buttons.ts. lgRim and lgPress need the
 * element to be positioned (relative / absolute / fixed); give it that yourself.
 *
 * No backticks inside the CSS comments below (scripts/check-styled-comments.js).
 */
import { css, keyframes, DefaultTheme } from 'styled-components';

type P = { theme: DefaultTheme };
const dk = (p: P) => p.theme.colorScheme === 'dark';

/** A small spring that passes its end a little and settles: the press squish's way back. */
export const LG_POP = 'linear(0, 0.2 8%, 0.55 18%, 0.85 28%, 1.03 38%, 1.07 45%, 1.05 54%, 1.01 64%, 0.995 76%, 1)';

/* The frosted grain: a 140 px SVG noise tile. White specks on dark glass, dark specks on light glass. */
const NOISE_ON_DARK = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .07 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
const NOISE_ON_LIGHT = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .09 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/* ── Colours, both themes ── */
export const lgHair = (p: P) => (dk(p) ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.09)');
export const lgControlBg = (p: P) => (dk(p) ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.58)');
export const lgControlHover = (p: P) => (dk(p) ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)');
export const lgTrack = (p: P) => (dk(p) ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)');
/** The green the glass parts use for glows and the focus ring. */
export const lgGreen = (p: P) => (dk(p) ? '#4acd8b' : '#12855f');
export const lgCardSheen = (p: P) => (dk(p)
  ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0) 55%)'
  : 'linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0) 55%)');
const surfaceSheen = (p: P) => (dk(p)
  ? 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 22%, rgba(255,255,255,0) 45%)'
  : 'linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.18) 28%, rgba(255,255,255,0) 60%)');
const smallShadow = (p: P) => (dk(p)
  ? '0 1px 1px rgba(0,0,0,0.25), 0 10px 24px -12px rgba(0,0,0,0.6)'
  : '0 1px 2px rgba(22,25,23,0.06), 0 8px 20px -10px rgba(22,25,23,0.24)');
/** Edge ring + bottom reflection + soft drop shadow of a glass surface (a panel can drop it while it is closed). */
export const lgSurfaceShadow = (p: P) => (dk(p)
  ? '0 0 0 1px rgba(0,0,0,0.35), inset 0 -18px 28px -24px rgba(255,255,255,0.24), 0 1px 2px rgba(0,0,0,0.3), 0 28px 60px -12px rgba(0,0,0,0.6)'
  : '0 0 0 1px rgba(22,25,23,0.08), inset 0 -16px 24px -20px rgba(255,255,255,0.95), 0 1px 2px rgba(22,25,23,0.06), 0 24px 56px -12px rgba(22,25,23,0.24)');
/** A soft colour glow from the top edge of a card (it replaces the old 3 px coloured top line). */
export const lgTopGlow = (colour: string) => `radial-gradient(120% 100% at 50% 0%, ${colour}, transparent 70%)`;

/** The contour rim: a 1 px ring, bright on the top edge, faint on the sides, a softer light on the bottom edge. */
export const lgRim = css`
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: ${(p: P) => (dk(p)
      ? 'linear-gradient(180deg, rgba(255,255,255,0.40) 0%, rgba(255,255,255,0.09) 16%, rgba(255,255,255,0.04) 58%, rgba(255,255,255,0.20) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.45) 18%, rgba(255,255,255,0.22) 60%, rgba(255,255,255,0.85) 100%)')};
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
    z-index: 4;
  }
`;

/** Windows, drawers and the dock: the frosted surface with the live blur. (Lists and menus that open next to buttons use
 *  lgPopover below.) */
export const lgSurface = (blur = 34) => css`
  isolation: isolate;
  background-color: ${(p: P) => (dk(p) ? 'rgba(24,25,26,0.56)' : 'rgba(250,251,250,0.60)')};
  background-image: ${(p: P) => (dk(p) ? NOISE_ON_DARK : NOISE_ON_LIGHT)}, ${surfaceSheen};
  backdrop-filter: blur(${blur}px) saturate(125%);
  -webkit-backdrop-filter: blur(${blur}px) saturate(125%);
  box-shadow: ${lgSurfaceShadow};
  ${lgRim}
`;

/** [STALE-PRESS 2026-11-02, user] Lists and menus that open NEXT TO buttons (dropdown lists, Edit Columns, Download, the date
 *  pickers, the profile menu, the deals list): the same glass as lgSurface but WITHOUT the live blur, on a SOLID tint (the sheen,
 *  rim, grain and shadow make the glass; at 97 % and 98.5 % white text behind a dark list still showed through faintly, and the
 *  blur used to hide that). Why: on the user's screen Chrome redrew the reach of a list's live
 *  blur (3 x the blur: 78 px around a 26 px blur) with OLD pictures of the buttons there, so "November 2026" and "Edit Columns"
 *  showed a piece of their old press light cut by a straight line while "All Accounts" was open. This list had the same kind of
 *  Chrome blur glitch once before (a white box, see GlassSelect). Windows and drawers keep the live blur: their shade covers the
 *  whole page, so Chrome redraws all of it. */
export const lgPopover = css`
  isolation: isolate;
  background-color: ${(p: P) => (dk(p) ? 'rgb(30,31,32)' : 'rgb(250,251,250)')};
  background-image: ${(p: P) => (dk(p) ? NOISE_ON_DARK : NOISE_ON_LIGHT)}, ${surfaceSheen};
  box-shadow: ${lgSurfaceShadow};
  ${lgRim}
`;

/** A card that sits ON a glass surface. No blur of its own: the surface under it already did that. */
export const lgCard = css`
  background-color: ${(p: P) => (dk(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.58)')};
  background-image: ${lgCardSheen};
  box-shadow: ${smallShadow};
  ${lgRim}
`;

/** [LIQUID-GLASS 2026-11-02] A glass card that sits on a page's own card (tiles, frames, boxes inside a window). The rim is the
 *  border itself (brighter on the top edge) plus a light top line, so it also works on boxes that scroll (no ::before). */
export const lgTile = css`
  border: 1px solid;
  border-color: ${(p: P) => (dk(p)
    ? 'rgba(255,255,255,0.15) rgba(255,255,255,0.07) rgba(255,255,255,0.10)'
    : 'rgba(22,25,23,0.05) rgba(22,25,23,0.09) rgba(22,25,23,0.12)')};
  background-color: ${(p: P) => (dk(p) ? 'rgba(255,255,255,0.045)' : 'rgba(255,255,255,0.72)')};
  background-image: ${lgCardSheen};
  box-shadow: ${(p: P) => (dk(p)
    ? 'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -16px 24px -22px rgba(255,255,255,0.20), 0 1px 2px rgba(0,0,0,0.3), 0 12px 26px -16px rgba(0,0,0,0.6)'
    : 'inset 0 1px 0 #ffffff, 0 1px 2px rgba(22,25,23,0.05), 0 10px 22px -14px rgba(22,25,23,0.22)')};
`;

/** [LIQUID-GLASS 2026-11-02] Light theme only: a glass control on a WHITE part of a page gets a thin grey edge (white glass alone
 *  is lost there — the top bar lesson). Put it after the control's own mixin; it sets the shadow. */
export const lgOnWhite = css`
  ${(p: P) => (dk(p) ? '' : 'box-shadow: 0 0 0 1px rgba(22,25,23,0.13), 0 1px 2px rgba(22,25,23,0.06), 0 8px 18px -10px rgba(22,25,23,0.22);')}
`;

/** A capsule of glass that is not clickable (a label pill). */
export const lgPill = css`
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  box-shadow: ${smallShadow};
  ${lgRim}
`;

/** A capsule / round glass control (close button): the pill, a little brighter under the mouse. */
export const lgControl = css`
  ${lgPill}
  &:hover:not(:disabled) { background-color: ${lgControlHover}; }
`;

/** Press feel: the control squishes to 95 % on the press and springs back; a soft light shows where it was pressed. */
export const lgPress = css`
  transition: transform 500ms ${LG_POP}, background-color 150ms ease, box-shadow 200ms ease, filter 120ms ease, opacity 120ms ease;
  &:active:not(:disabled) {
    transform: scale(0.95);
    transition: transform 90ms ease-out, background-color 150ms ease, box-shadow 200ms ease;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(90px circle at var(--lg-x, 50%) var(--lg-y, 50%), ${(p: P) => (dk(p) ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.85)')}, transparent 65%);
    opacity: 0;
    transition: opacity 700ms ease;
    pointer-events: none;
    z-index: 3;
  }
  &[data-lg-lit]::after {
    opacity: 1;
    transition: opacity 0s;
  }
  @media (prefers-reduced-motion: reduce) {
    &:active:not(:disabled) { transform: none; }
  }
`;

/** An input box that looks pressed into the glass; the box you type in gets a soft green ring and glow. */
export const lgSunken = css`
  border: 1px solid transparent;
  background: ${(p: P) => (dk(p) ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  box-shadow: ${(p: P) => (dk(p)
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    box-shadow: ${(p: P) => (dk(p)
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')},
      0 0 0 4px color-mix(in srgb, ${lgGreen} 16%, transparent),
      0 0 18px color-mix(in srgb, ${lgGreen} 24%, transparent);
  }
`;

const chaseSweep = keyframes`
  to { transform: translateX(260%); }
`;
/** A soft light that keeps moving along a progress bar while its work runs. The bar must be positioned. */
export const lgChase = css`
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 45%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
    transform: translateX(-120%);
    animation: ${chaseSweep} 2.3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    &::after { display: none; }
  }
`;

/** Smooth (squircle) corners where the browser has them (Chrome / Edge 139+): the radius is raised so the curve reads
 *  as the same size. Other browsers keep the plain radius. */
export const lgSquircle = (r: number) => css`
  border-radius: ${r}px;
  @supports (corner-shape: squircle) {
    border-radius: ${Math.round(r * 1.6)}px;
    corner-shape: squircle;
    &::before { corner-shape: squircle; }
  }
`;

/* ── Buttons: capsules, 34 px tall. They replace buttonPrimary / buttonSecondary / buttonDanger on the glass parts. ── */
const lgButtonShape = css`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 34px;
  padding: 0 16px;
  box-sizing: border-box;
  border: none;
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  & > * { position: relative; z-index: 1; }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  ${lgRim}
  ${lgPress}
`;

/** Plain action: a glass capsule. */
export const lgButtonSecondary = css`
  ${lgButtonShape}
  color: ${(p: P) => p.theme.text};
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  box-shadow: ${smallShadow};
  &:hover:not(:disabled) { background-color: ${lgControlHover}; }
`;

/** Main green action: a glossy green capsule. On a PureInk page (theme.primaryDeep) the light theme takes a deeper
 *  green so the white words read well. */
export const lgButtonPrimary = css`
  ${lgButtonShape}
  color: #fff;
  background: linear-gradient(180deg, rgba(255,255,255,0.34), rgba(255,255,255,0) 52%), ${(p: P) => (dk(p)
    ? 'linear-gradient(180deg, #3ccf8f 0%, #1f9e66 55%, #178a57 100%)'
    : p.theme.primaryDeep
      ? 'linear-gradient(180deg, #1f8f6e 0%, #16735a 55%, #136650 100%)'
      : 'linear-gradient(180deg, #22c495 0%, #12936b 55%, #0d7f5d 100%)')};
  box-shadow:
    0 8px 22px -6px ${(p: P) => (dk(p) ? 'rgba(74,205,139,0.35)' : 'rgba(16,185,129,0.38)')},
    inset 0 1px 0 rgba(255,255,255,0.55),
    inset 0 -1px 0 rgba(0,0,0,0.16);
  &:hover:not(:disabled) { filter: brightness(1.05); }
`;

/** Destructive action: a red-tinted glass capsule. */
export const lgButtonDanger = css`
  ${lgButtonShape}
  color: ${(p: P) => p.theme.danger};
  background-color: color-mix(in srgb, ${(p: P) => p.theme.danger} 12%, transparent);
  box-shadow: ${smallShadow};
  &::before {
    background: linear-gradient(180deg,
      color-mix(in srgb, ${(p: P) => p.theme.danger} 75%, transparent),
      color-mix(in srgb, ${(p: P) => p.theme.danger} 15%, transparent) 45%,
      color-mix(in srgb, ${(p: P) => p.theme.danger} 35%, transparent));
  }
  &:hover:not(:disabled) { background-color: color-mix(in srgb, ${(p: P) => p.theme.danger} 18%, transparent); }
`;

/** Attribute that turns on the press light for an element (see startLiquidPress):
 *  styled.button.attrs<LgPressProps>(LG_PRESS), or data-lg-press="" on the element. */
export type LgPressProps = { 'data-lg-press'?: string };
export const LG_PRESS: LgPressProps = { 'data-lg-press': '' };

/** One listener for the whole app: on a press of anything marked data-lg-press, put the light where the pointer is, and
 *  take it away on release (it then fades over 0.7 s). Called once from index.tsx. */
export function startLiquidPress(): void {
  if (typeof document === 'undefined') return;
  const lit = new Set<HTMLElement>();
  document.addEventListener('pointerdown', (e) => {
    const el = (e.target as Element | null)?.closest?.('[data-lg-press]') as HTMLElement | null;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // [APP-FIT 2026-10-26] Under the app zoom the box is drawn bigger or smaller than its own CSS px: scale back.
    const z = el.offsetWidth > 0 ? r.width / el.offsetWidth : 1;
    el.style.setProperty('--lg-x', `${(e.clientX - r.left) / (z || 1)}px`);
    el.style.setProperty('--lg-y', `${(e.clientY - r.top) / (z || 1)}px`);
    el.setAttribute('data-lg-lit', '');
    lit.add(el);
  }, true);
  const off = () => {
    lit.forEach((el) => el.removeAttribute('data-lg-lit'));
    lit.clear();
  };
  document.addEventListener('pointerup', off, true);
  document.addEventListener('pointercancel', off, true);
}
